import { IconButton, ListItemSecondaryAction, Modal } from '@material-ui/core';
import Backdrop from '@material-ui/core/Backdrop/Backdrop';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography/Typography';
import EditIcon from '@material-ui/icons/Edit';
import { Formik } from 'formik';
import React, { useReducer } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { SnackbarComponent } from '../../../../../core/utils/components/snackbar-component';
import { IPaymentProvider } from '../../../domain/entities/branch-entity';
import { editPaymentFields } from '../../redux/actions/branch-actions';
import {
  paymentFieldsActionTypes,
  paymentFieldsInitialState,
  paymentFieldsReducer
} from './payment_fields.reducer';
import PaymentFieldsForm from './payment_fields_form.component';
import { useStyles } from './payment_providers.styles';

export function EditPaymentFields({
  branchId,
  paymentProvider
}: {
  paymentProvider: IPaymentProvider;
  branchId: string;
}) {
  const classes = useStyles();
  const reduxDispatch = useDispatch();
  const [state, dispatch] = useReducer(paymentFieldsReducer, paymentFieldsInitialState);

  const handleConfirmationOpen = () =>
    dispatch({
      type: paymentFieldsActionTypes.SET_OPEN,
      payload: true
    });
  const handleConfirmationClose = () =>
    dispatch({
      type: paymentFieldsActionTypes.SET_OPEN,
      payload: false
    });

  const showSnackbar = (status = 'success', description = 'Custom fields updated') => {
    dispatch({
      type: paymentFieldsActionTypes.OPEN_SNACKBAR,
      payload: {
        status,
        description
      }
    });
  };

  const closeSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    dispatch({
      type: paymentFieldsActionTypes.CLOSE_SNACKBAR
    });
  };

  const closeBackdrop = () => {
    dispatch({
      type: paymentFieldsActionTypes.SET_BACKDROP,
      payload: false
    });
  };

  const validationSchema = Yup.object({
    merchant_id: Yup.string('Enter the store id')
      .min(1, 'Must be at least 1 character')
      .max(50, 'Must be less than 50 characters')
      .required("Please enter the Store ID"),
    acquirer_id: Yup.string('Enter the pos terminal id')
      .min(1, 'Must be at least 1 character')
      .max(50, 'Must be less than 50 characters')
      .required("Please enter the POS Terminal ID"),
    payment_provider: Yup.string('Enter the payment provider')
      .min(1, 'Must be at least 1 character')
      .max(50, 'Must be less than 50 characters')
      .required("Please enter the payment provider name")
  });

  const submit = (data) => {
    closeBackdrop();
    const props = { closeBackdrop, showSnackbar };
    reduxDispatch(
      editPaymentFields(branchId, data.merchant_id, data.acquirer_id, data.payment_provider, props)
    );
    handleConfirmationClose();
  };

  const values: IPaymentProvider = {
    merchant_id: paymentProvider?.merchant_id,
    acquirer_id: paymentProvider?.acquirer_id,
    payment_provider: paymentProvider?.payment_provider
  };

  return (
    <>
      <List data-testid="EDIT_PAYMENT_DETAILS" className={classes.root}>
        <ListItem>
          <Fields paymentFields={paymentProvider} />
          <ListItemSecondaryAction>
            <IconButton
              className={classes.margin}
              edge="end"
              aria-label="comments"
              onClick={handleConfirmationOpen}
            >
              <EditIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      </List>
      <>
        <Backdrop
          className={classes.backdrop}
          open={state.backdrop}
          invisible={false}
          onClick={closeBackdrop}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <SnackbarComponent
          open={state.snackbar}
          onClose={closeSnackbar}
          severity={state.message.status}
          message={state.message.description}
        />
      </>

      <Modal
        aria-labelledby="unstyled-modal-title"
        aria-describedby="unstyled-modal-description"
        open={state.open}
        onClose={handleConfirmationClose}
        BackdropComponent={Backdrop}
      >
        <Box className={classes.modal}>
          <h2 id="unstyled-modal-title">Payment Provider Configuration</h2>

          <Formik
            initialValues={values}
            validationSchema={validationSchema}
            onSubmit={submit}
            component={PaymentFieldsForm}
          />

          <Button
            className={classes.marginCancel}
            variant="outlined"
            size="large"
            color="secondary"
            fullWidth={true}
            onClick={handleConfirmationClose}
          >
            Cancel
          </Button>
        </Box>
      </Modal>
    </>
  );
}

function Fields({ paymentFields }: { paymentFields: IPaymentProvider }) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" component="div">
          Store ID : {paymentFields?.merchant_id || 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" component="div">
          POS Terminal ID : {paymentFields?.acquirer_id || 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" component="div">
          Payment Provider : {paymentFields?.payment_provider || 'N/A'}
        </Typography>
      </Grid>
    </Grid>
  );
}
